import { supabase } from '@/integrations/supabase/client';

export interface ReportParams {
  startDate?: string; // YYYY-MM-DD
  endDate?: string;   // YYYY-MM-DD
  companyId?: string;
  limit?: number;
}

export interface ReportResult<T> {
  rows: T[];
  total?: number;
  meta?: Record<string, unknown>;
}

// Generic RPC caller with consistent error handling
async function callRpc<T>(fn: string, params: Record<string, any>): Promise<T[]> {
  const { data, error } = await supabase.rpc(fn, params as any);
  if (error) throw error;
  return (data as T[]) || [];
}

export interface TopClientRow {
  client_name: string;
  revenue: number;
  transactions: number;
}

export const reportsApi = { 
  /**
   * Payment Method Distribution
   * Returns { methods, trend }
   */
  async getPaymentMethodDistribution(params: ReportParams): Promise<ReportResult<{ methods: any[]; trend: any[] }>> {
    // Prefer RPC
    try {
      const rows = await callRpc<{ methods: any[]; trend: any[] }>('get_payment_method_distribution', {
        p_start_date: params.startDate || null,
        p_end_date: params.endDate || null,
        p_company_id: params.companyId || null,
      });
      // When RPC returns a single object, normalize
      if (Array.isArray(rows) && rows.length === 1 && (rows[0] as any).methods) {
        const only = rows[0] as any;
        return { rows: [{ methods: only.methods, trend: only.trend }] };
      }
      // When RPC returns structured arrays directly
      if (Array.isArray(rows) && rows.length > 0) {
        return { rows } as any;
      }
      return { rows: [{ methods: [], trend: [] }] };
    } catch (e) {
      // Fallback: two views (methods and trend)
      const { data: methodsView } = await supabase
        .from('vw_payment_method_distribution_methods')
        .select('*');
      const { data: trendView } = await supabase
        .from('vw_payment_method_distribution_trend')
        .select('*');
      return { rows: [{ methods: methodsView || [], trend: trendView || [] }] };
    }
  },

  /**
   * Net Cash Flow Trend
   * Returns transactions list used to compute monthly net cash flow
   */
  async getNetCashFlowTrend(params: ReportParams): Promise<ReportResult<any>> {
    try {
      const rows = await callRpc<any>('get_monthly_cash_flow', {
        p_start_date: params.startDate || null,
        p_end_date: params.endDate || null,
        p_company_id: params.companyId || null,
      });
      return { rows };
    } catch (e) {
      const { data } = await supabase
        .from('vw_monthly_cash_flow')
        .select('date,payment_date,type,amount,is_paid,paid');
      return { rows: (data || []) as any[] };
    }
  },

  async getTopClientsByRevenue(params: ReportParams): Promise<ReportResult<TopClientRow>> {
    // Preferred path: RPC. Ensure a matching SQL function exists in DB.
    try {
      const rows = await callRpc<TopClientRow>('get_top_clients_by_revenue', {
        p_start_date: params.startDate || null,
        p_end_date: params.endDate || null,
        p_company_id: params.companyId || null,
        p_limit: params.limit ?? 10,
      });
      return { rows };
    } catch (e) {
      // Fallback: attempt select from a materialized view, if present
      const { data, error } = await supabase
        .from('vw_top_clients_by_revenue')
        .select('client_name, revenue, transactions')
        .limit(params.limit ?? 10);
      if (error) throw e; // bubble the RPC error if fallback also fails
      return { rows: (data || []) as TopClientRow[] };
    }
  },
};
